import { useQueryClient } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import { useGetIdentity, useGetOne, useNotify, useTranslate } from "ra-core";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import type { TimelineItem } from "../domain/timeline";
import type { CallRow } from "./api/types";
import { canEditCallOutcome } from "./callEditWindow";
import { CallOutcomeDialog } from "./todo/CallOutcomeDialog";

type CompanyTzEmail = {
  id: number;
  timezone?: string | null;
  email?: string | null;
};

/**
 * Timeline call item extras (spec 04 §7.11): « Modifier le résultat ». GAVION small fixes 5 (owner decision
 * 2026-10-14): the CRM records no calls (Granola takes the notes), so there is no recording player any more.
 */
export const CallTimelineActions = ({
  item,
  now = () => new Date(),
}: {
  item: TimelineItem;
  now?: () => Date;
}) => {
  const t = useTranslate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { identity } = useGetIdentity();
  const [open, setOpen] = useState(false);
  const canEdit = canEditCallOutcome(item, identity?.id, now());
  const { data: call } = useGetOne<CallRow>(
    "calls",
    { id: item.source_id },
    { enabled: open },
  );
  const { data: company } = useGetOne<CompanyTzEmail>(
    "companies",
    { id: item.company_id },
    { enabled: open },
  );
  if (!canEdit) return null;
  return (
    <div className="flex flex-col items-start gap-2">
      {canEdit ? (
        <Button
          variant="ghost"
          size="sm"
          className="h-auto px-1 py-0.5"
          onClick={() => setOpen(true)}
        >
          <Pencil className="size-3.5" aria-hidden="true" />
          {t("gavion.dialer.timeline.edit_outcome")}
        </Button>
      ) : null}
      {open && call ? (
        <CallOutcomeDialog
          call={call}
          open
          onOpenChange={setOpen}
          leadTimezone={company?.timezone ?? undefined}
          leadEmail={company?.email ?? null}
          now={now}
          onDone={() => {
            setOpen(false);
            notify("gavion.dialer.todo.saved", { type: "success" });
            for (const key of ["v_timeline", "calls", "companies"])
              void queryClient.invalidateQueries({ queryKey: [key] });
          }}
        />
      ) : null}
    </div>
  );
};
